import { useCallback, useState } from 'react';
import type { NodeMouseHandler } from '@xyflow/react';
import type { LayoutResult } from './layout-treemap';
import {
  ascendTreemapDrill,
  drillIntoTreemapNode,
  drillToTreemapDepth,
  fitTreemapOverview,
} from './treemap-interaction-helpers';
import type {
  SetZoomLevel,
  TreemapReactFlowInstance,
} from './treemap-interaction-types';
import type { ParsedTreemap, TreemapFlowNode } from './treemap-types';
import type { TreemapViewportRect } from './treemap-view-model';
import { useTreemapResizeRefit } from './useTreemapResizeRefit';
import type { RefObject } from 'react';

interface UseTreemapDrillControlsInput {
  canvasWrapperRef: RefObject<HTMLDivElement | null>;
  layout: LayoutResult | null;
  parentMap: Map<string, string>;
  parsed: ParsedTreemap | null;
  reactFlowInstance: TreemapReactFlowInstance | null;
  rectById: Map<string, TreemapViewportRect>;
  setZoomLevel: SetZoomLevel;
}

interface TreemapDrillControls {
  ascendDrill: () => void;
  drillIntoNode: (nodeId: string) => void;
  drillPathIds: string[];
  drillToDepth: (index: number) => void;
  onNodeClick: NodeMouseHandler<TreemapFlowNode>;
  resetView: () => void;
}

export function useTreemapDrillControls({
  canvasWrapperRef,
  parentMap,
  parsed,
  reactFlowInstance,
  rectById,
  setZoomLevel,
}: UseTreemapDrillControlsInput): TreemapDrillControls {
  const [drillPathIds, setDrillPathIds] = useState<string[]>([]);

  useTreemapResizeRefit({
    canvasWrapperRef,
    reactFlowInstance,
    drillPathIds,
    rectById,
    setZoomLevel,
  });

  const resetView = useCallback((): void => {
    setDrillPathIds([]);
    fitTreemapOverview(reactFlowInstance);
  }, [reactFlowInstance]);

  const drillIntoNode = useCallback(
    (nodeId: string): void => {
      drillIntoTreemapNode({
        parsed,
        parentMap,
        rectById,
        reactFlowInstance,
        setDrillPathIds,
        nodeId,
      });
    },
    [parsed, parentMap, rectById, reactFlowInstance],
  );

  const drillToDepth = useCallback(
    (index: number): void => {
      drillToTreemapDepth({
        drillPathIds,
        index,
        reactFlowInstance,
        rectById,
        resetView,
        setDrillPathIds,
      });
    },
    [drillPathIds, reactFlowInstance, rectById, resetView],
  );

  const ascendDrill = useCallback((): void => {
    setDrillPathIds((previous) =>
      ascendTreemapDrill({
        previous,
        reactFlowInstance,
        rectById,
      }),
    );
  }, [reactFlowInstance, rectById]);

  const onNodeClick: NodeMouseHandler<TreemapFlowNode> = useCallback(
    (_event, node): void => {
      drillIntoNode(node.id);
    },
    [drillIntoNode],
  );

  return {
    ascendDrill,
    drillIntoNode,
    drillPathIds,
    drillToDepth,
    onNodeClick,
    resetView,
  };
}
